import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ShieldCheck, Plus, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Applicant", "Residence", "Employment & Income", "Co-applicant & Guarantor",
                "Pets & Vehicles", "References & History", "Disclosures & Sign"];

const empty = {
  applicant: { legal_first_name: "", legal_middle_name: "", legal_last_name: "",
    other_names_used: "", date_of_birth: "", email: "", phone: "",
    ssn_last4: "", id_type: "drivers_license", id_number_last4: "", id_state_or_country: "CA" },
  current_address: { street: "", unit: "", city: "", state: "CA", zip: "",
    landlord_name: "", landlord_phone: "", landlord_email: "", monthly_rent: "", move_in: "" },
  previous_addresses: [],
  employment: [{ employer_name: "", position: "", start_date: "", end_date: "",
    supervisor_name: "", supervisor_phone: "", monthly_gross_income: "" }],
  additional_income: [],
  references: [{ name: "", relationship: "", phone: "", email: "", years_known: "" }],
  pets: [],
  vehicles: [],
  co_applicants: [],
  guarantor: null,
  emergency_contact: { name: "", relationship: "", phone: "", email: "" },
  history: { has_been_evicted: false, eviction_explanation: "", has_filed_bankruptcy: false,
    bankruptcy_explanation: "", has_conviction: false, conviction_explanation: "",
    is_smoker: false, receives_housing_subsidy: false },
  desired_move_in: "",
  notes_to_landlord: "",
};

const HField = ({ label, children, required, hint }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
      {label} {required && <span className="text-[#EF4444]">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] text-[#4A5568] mt-1">{hint}</p>}
  </div>
);

export default function PublicApplicationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [data, setData] = useState(empty);
  const [accepted, setAccepted] = useState({});
  const [signedName, setSignedName] = useState("");
  const [esign, setEsign] = useState(false);
  const [bg, setBg] = useState(false);
  const [credit, setCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/applications/by-token/${token}`)
      .then(({ data }) => {
        setMeta(data);
        setData((d) => ({
          ...d,
          applicant: { ...d.applicant,
            legal_first_name: data.screening.applicant_first_name || "",
            legal_last_name: data.screening.applicant_last_name || "",
            email: data.screening.applicant_email || "" },
        }));
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, [token]);

  const update = (path, value) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d));
    let cur = next;
    const parts = path.split(".");
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      cur[p] = cur[p] === undefined || cur[p] === null ? {} : cur[p];
      cur = cur[p];
    }
    cur[parts.at(-1)] = value;
    return next;
  });

  const submit = async () => {
    setSubmitting(true);
    try {
      // Coerce numeric fields
      const clean = JSON.parse(JSON.stringify(data));
      const num = (v) => v === "" || v === null ? undefined : Number(v);
      const blank = (v) => (v === "" || v == null ? undefined : v);
      if (clean.current_address.monthly_rent !== "") clean.current_address.monthly_rent = num(clean.current_address.monthly_rent);
      else delete clean.current_address.monthly_rent;
      clean.previous_addresses = clean.previous_addresses.map((p) => ({ ...p, monthly_rent: p.monthly_rent ? num(p.monthly_rent) : undefined }));
      clean.employment = clean.employment.map((e) => ({ ...e, monthly_gross_income: num(e.monthly_gross_income) }));
      clean.additional_income = clean.additional_income.map((i) => ({ ...i, monthly_amount: num(i.monthly_amount) }));
      clean.co_applicants = clean.co_applicants.map((c) => ({ ...c, monthly_gross_income: num(c.monthly_gross_income), email: blank(c.email) ?? c.email }));
      if (clean.guarantor) clean.guarantor.monthly_gross_income = num(clean.guarantor.monthly_gross_income);
      clean.references = clean.references.map((r) => ({ ...r, years_known: r.years_known ? num(r.years_known) : undefined, email: blank(r.email) }));
      clean.pets = clean.pets.map((p) => ({ ...p, weight_lbs: p.weight_lbs ? num(p.weight_lbs) : undefined }));
      // Strip empty-string optional emails (Pydantic EmailStr rejects "")
      clean.emergency_contact = { ...clean.emergency_contact, email: blank(clean.emergency_contact.email) };

      await axios.post(`${API}/applications/by-token/${token}`, {
        application: clean,
        signature: {
          typed_full_legal_name: signedName,
          accepted_disclosure_keys: Object.keys(accepted).filter((k) => accepted[k]),
          consented_to_esign: esign,
          consented_to_background_check: bg,
          consented_to_credit_check: credit,
        },
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  if (error) return <Shell><div className="text-[#EF4444]" data-testid="apply-error">{error}</div></Shell>;
  if (!meta) return <Shell><div className="font-overline text-[#4A5568]">Loading…</div></Shell>;
  if (done) return <DoneScreen meta={meta} />;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto">
        <div className="font-overline text-[#4A5568] mb-3">California rental application</div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#0A192F] mb-1" data-testid="apply-property">{meta.screening.property_address}</h1>
        <p className="text-sm text-[#4A5568]">Submitted by {meta.screening.submitted_by_name}{meta.screening.submitted_by_company ? ` · ${meta.screening.submitted_by_company}` : ""}.</p>

        <div className="mt-6 mb-2 flex flex-wrap gap-1.5">
          {STEPS.map((label, i) => (
            <div key={label} className={`flex-1 min-w-[8rem] h-1 rounded-sm ${step >= i ? "bg-[#0A192F]" : "bg-[#0A192F]/15"}`} />
          ))}
        </div>
        <div className="text-xs font-mono text-[#4A5568] mb-6">STEP {step + 1} OF {STEPS.length} · {STEPS[step]}</div>

        <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6 sm:p-8">
          {step === 0 && <ApplicantStep data={data} update={update} />}
          {step === 1 && <ResidenceStep data={data} update={update} setData={setData} />}
          {step === 2 && <EmploymentStep data={data} update={update} setData={setData} />}
          {step === 3 && <CoStep data={data} update={update} setData={setData} />}
          {step === 4 && <PetVehicleStep data={data} update={update} setData={setData} />}
          {step === 5 && <RefHistoryStep data={data} update={update} setData={setData} />}
          {step === 6 && <SignStep meta={meta} data={data} accepted={accepted} setAccepted={setAccepted}
                                    signedName={signedName} setSignedName={setSignedName}
                                    esign={esign} setEsign={setEsign} bg={bg} setBg={setBg}
                                    credit={credit} setCredit={setCredit} />}
          <div className="flex justify-between mt-8 pt-6 border-t border-[#0A192F]/10">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="rounded-sm" data-testid="apply-back">
              <ChevronLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep((s) => s + 1)} className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded-sm" data-testid="apply-next">
                Next <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded-sm" data-testid="apply-submit">
                {submitting ? "Submitting…" : "Sign & submit application"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <header className="border-b border-[#0A192F]/10 bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#4A5568]">
            <ShieldCheck className="h-4 w-4 text-[#22C55E]" /> California-compliant · TLS encrypted
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">{children}</main>
    </div>
  );
}

function ApplicantStep({ data, update }) {
  const a = data.applicant;
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-[#0A192F]">Applicant Information</h2>
      <p className="text-sm text-[#4A5568]">All fields marked with * are required. Your data is encrypted in transit and at rest.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <HField label="Legal first name" required><Input value={a.legal_first_name} onChange={(e) => update("applicant.legal_first_name", e.target.value)} className="h-11 rounded-sm" data-testid="a-first" /></HField>
        <HField label="Middle"><Input value={a.legal_middle_name} onChange={(e) => update("applicant.legal_middle_name", e.target.value)} className="h-11 rounded-sm" /></HField>
        <HField label="Legal last name" required><Input value={a.legal_last_name} onChange={(e) => update("applicant.legal_last_name", e.target.value)} className="h-11 rounded-sm" data-testid="a-last" /></HField>
      </div>
      <HField label="Other names used (maiden, prior legal name)" hint="Optional. Used for accurate consumer-report matching."><Input value={a.other_names_used} onChange={(e) => update("applicant.other_names_used", e.target.value)} className="h-11 rounded-sm" /></HField>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <HField label="Date of birth" required><Input type="date" value={a.date_of_birth} onChange={(e) => update("applicant.date_of_birth", e.target.value)} className="h-11 rounded-sm" data-testid="a-dob" /></HField>
        <HField label="Email" required><Input type="email" value={a.email} onChange={(e) => update("applicant.email", e.target.value)} className="h-11 rounded-sm" data-testid="a-email" /></HField>
        <HField label="Phone" required><Input value={a.phone} onChange={(e) => update("applicant.phone", e.target.value)} className="h-11 rounded-sm" data-testid="a-phone" /></HField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <HField label="SSN/ITIN — last 4 only" required hint="Full SSN is collected at lease signing through a separate channel.">
          <Input value={a.ssn_last4} maxLength={4} onChange={(e) => update("applicant.ssn_last4", e.target.value.replace(/\D/g, ""))} className="h-11 rounded-sm font-mono" data-testid="a-ssn4" />
        </HField>
        <HField label="ID type" required>
          <Select value={a.id_type} onValueChange={(v) => update("applicant.id_type", v)}>
            <SelectTrigger className="h-11 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="drivers_license">Driver's license</SelectItem>
              <SelectItem value="state_id">State ID</SelectItem>
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </HField>
        <HField label="ID — last digits" required><Input value={a.id_number_last4} maxLength={8} onChange={(e) => update("applicant.id_number_last4", e.target.value)} className="h-11 rounded-sm font-mono" /></HField>
        <HField label="State / country" required><Input value={a.id_state_or_country} onChange={(e) => update("applicant.id_state_or_country", e.target.value)} className="h-11 rounded-sm" /></HField>
      </div>
      <div className="bg-[#F3EFEA] p-4 rounded-sm text-xs text-[#4A5568] flex gap-2">
        <AlertCircle className="h-4 w-4 text-[#0A192F] shrink-0 mt-0.5" />
        <span>California prohibits discrimination based on immigration status, primary language, or any protected category. ITIN is accepted in lieu of SSN.</span>
      </div>
    </div>
  );
}

function ResidenceStep({ data, update, setData }) {
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-2xl text-[#0A192F]">Residence History</h2>
      <AddressBlock label="Current residence" v={data.current_address} onChange={(field, val) => update(`current_address.${field}`, val)} testidPrefix="cur" />
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Previous residences (last 3 years)</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setData((d) => ({ ...d, previous_addresses: [...d.previous_addresses, { street: "", city: "", state: "CA", zip: "", move_in: "", move_out: "" }] }))} data-testid="add-prev-addr">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add previous
          </Button>
        </div>
        <div className="space-y-4">
          {data.previous_addresses.map((p, i) => (
            <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 bg-[#F3EFEA]/40 relative">
              <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-[#EF4444]" onClick={() => setData((d) => ({ ...d, previous_addresses: d.previous_addresses.filter((_, j) => j !== i) }))}>
                <Trash2 className="h-4 w-4" />
              </Button>
              <AddressBlock label={`Previous #${i + 1}`} showReason v={p}
                onChange={(field, val) => setData((d) => ({ ...d, previous_addresses: d.previous_addresses.map((x, j) => j === i ? { ...x, [field]: val } : x) }))} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddressBlock({ label, v, onChange, showReason, testidPrefix }) {
  return (
    <div>
      <div className="font-overline text-[#4A5568] mb-3">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
        <div className="sm:col-span-4"><HField label="Street" required><Input value={v.street} onChange={(e) => onChange("street", e.target.value)} className="h-11 rounded-sm" data-testid={testidPrefix && `${testidPrefix}-street`} /></HField></div>
        <div className="sm:col-span-2"><HField label="Unit"><Input value={v.unit || ""} onChange={(e) => onChange("unit", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-3"><HField label="City" required><Input value={v.city} onChange={(e) => onChange("city", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-1"><HField label="State" required><Input value={v.state} onChange={(e) => onChange("state", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-2"><HField label="ZIP" required><Input value={v.zip} onChange={(e) => onChange("zip", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-2"><HField label="Move-in"><Input type="date" value={v.move_in || ""} onChange={(e) => onChange("move_in", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-2"><HField label="Move-out"><Input type="date" value={v.move_out || ""} onChange={(e) => onChange("move_out", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-2"><HField label="Monthly rent ($)"><Input type="number" value={v.monthly_rent ?? ""} onChange={(e) => onChange("monthly_rent", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-3"><HField label="Landlord name"><Input value={v.landlord_name || ""} onChange={(e) => onChange("landlord_name", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        <div className="sm:col-span-3"><HField label="Landlord phone"><Input value={v.landlord_phone || ""} onChange={(e) => onChange("landlord_phone", e.target.value)} className="h-11 rounded-sm" /></HField></div>
        {showReason && <div className="sm:col-span-6"><HField label="Reason for leaving"><Input value={v.reason_for_leaving || ""} onChange={(e) => onChange("reason_for_leaving", e.target.value)} className="h-11 rounded-sm" /></HField></div>}
      </div>
    </div>
  );
}

function EmploymentStep({ data, setData }) {
  const addEmp = () => setData((d) => ({ ...d, employment: [...d.employment, { employer_name: "", position: "", start_date: "", end_date: "", supervisor_name: "", supervisor_phone: "", monthly_gross_income: "" }] }));
  const addInc = () => setData((d) => ({ ...d, additional_income: [...d.additional_income, { source_type: "housing_voucher_section_8", monthly_amount: "", description: "" }] }));
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-2xl text-[#0A192F]">Employment & income</h2>
      <div className="bg-[#F3EFEA] p-4 rounded-sm text-xs text-[#4A5568] flex gap-2">
        <AlertCircle className="h-4 w-4 text-[#0A192F] shrink-0 mt-0.5" />
        <span>Source-of-income protection (SB 329 / SB 222) — Housing vouchers, Section 8, and government benefits are considered income and may not be used to deny your application.</span>
      </div>
      <div className="space-y-4">
        {data.employment.map((e, i) => (
          <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 bg-[#F3EFEA]/40 relative">
            {i > 0 && <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-[#EF4444]" onClick={() => setData((d) => ({ ...d, employment: d.employment.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>}
            <div className="font-overline text-[#4A5568] mb-3">Employment #{i + 1}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <HField label="Employer" required><Input value={e.employer_name} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, employer_name: ev.target.value } : x) }))} className="h-11 rounded-sm" data-testid={`emp-${i}-name`} /></HField>
              <HField label="Position" required><Input value={e.position} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, position: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
              <HField label="Start date" required><Input type="date" value={e.start_date} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, start_date: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
              <HField label="End date (blank if current)"><Input type="date" value={e.end_date || ""} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, end_date: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
              <HField label="Supervisor"><Input value={e.supervisor_name || ""} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, supervisor_name: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
              <HField label="Supervisor phone"><Input value={e.supervisor_phone || ""} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, supervisor_phone: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
              <HField label="Monthly gross income ($)" required><Input type="number" value={e.monthly_gross_income} onChange={(ev) => setData((d) => ({ ...d, employment: d.employment.map((x, j) => j === i ? { ...x, monthly_gross_income: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addEmp} data-testid="add-emp"><Plus className="h-3.5 w-3.5 mr-1" /> Add employer</Button>

      <div>
        <div className="flex items-center justify-between mt-4 mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Other income (optional)</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addInc} data-testid="add-inc"><Plus className="h-3.5 w-3.5 mr-1" /> Add income</Button>
        </div>
        {data.additional_income.map((inc, i) => (
          <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 mb-3 bg-[#F3EFEA]/40 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <HField label="Source">
              <Select value={inc.source_type} onValueChange={(v) => setData((d) => ({ ...d, additional_income: d.additional_income.map((x, j) => j === i ? { ...x, source_type: v } : x) }))}>
                <SelectTrigger className="h-11 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="housing_voucher_section_8">Section 8 / housing voucher</SelectItem>
                  <SelectItem value="social_security">Social Security</SelectItem>
                  <SelectItem value="ssi_ssdi">SSI / SSDI</SelectItem>
                  <SelectItem value="unemployment">Unemployment</SelectItem>
                  <SelectItem value="child_support">Child support</SelectItem>
                  <SelectItem value="alimony">Alimony</SelectItem>
                  <SelectItem value="self_employment">Self-employment</SelectItem>
                  <SelectItem value="investment">Investment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </HField>
            <HField label="Monthly amount ($)"><Input type="number" value={inc.monthly_amount} onChange={(ev) => setData((d) => ({ ...d, additional_income: d.additional_income.map((x, j) => j === i ? { ...x, monthly_amount: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Description"><Input value={inc.description || ""} onChange={(ev) => setData((d) => ({ ...d, additional_income: d.additional_income.map((x, j) => j === i ? { ...x, description: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoStep({ data, setData }) {
  const addCo = () => setData((d) => ({ ...d, co_applicants: [...d.co_applicants, { legal_first_name: "", legal_last_name: "", relationship_to_primary: "", email: "", phone: "", date_of_birth: "", monthly_gross_income: "" }] }));
  const setGuar = () => setData((d) => ({ ...d, guarantor: d.guarantor || { legal_first_name: "", legal_last_name: "", relationship_to_applicant: "", email: "", phone: "", monthly_gross_income: "", employer_name: "" } }));
  const removeGuar = () => setData((d) => ({ ...d, guarantor: null }));
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-2xl text-[#0A192F]">Co-applicants & guarantor</h2>
      <p className="text-sm text-[#4A5568]">Optional. Add anyone over 18 who will live with you and contribute to rent, plus a guarantor (cosigner) if applicable.</p>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Co-applicants</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addCo} data-testid="add-co"><Plus className="h-3.5 w-3.5 mr-1" /> Add co-applicant</Button>
        </div>
        {data.co_applicants.map((c, i) => (
          <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 bg-[#F3EFEA]/40 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3 relative">
            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-[#EF4444]" onClick={() => setData((d) => ({ ...d, co_applicants: d.co_applicants.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
            <HField label="First name" required><Input value={c.legal_first_name} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, legal_first_name: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Last name" required><Input value={c.legal_last_name} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, legal_last_name: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Relationship" required><Input value={c.relationship_to_primary} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, relationship_to_primary: ev.target.value } : x) }))} className="h-11 rounded-sm" placeholder="Spouse / Roommate / etc." /></HField>
            <HField label="Date of birth" required><Input type="date" value={c.date_of_birth} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, date_of_birth: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Email" required><Input type="email" value={c.email} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, email: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Phone" required><Input value={c.phone} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, phone: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Monthly gross income ($)" required><Input type="number" value={c.monthly_gross_income} onChange={(ev) => setData((d) => ({ ...d, co_applicants: d.co_applicants.map((x, j) => j === i ? { ...x, monthly_gross_income: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Guarantor / cosigner</h3>
          {!data.guarantor ? (
            <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={setGuar} data-testid="add-guar"><Plus className="h-3.5 w-3.5 mr-1" /> Add guarantor</Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" className="text-[#EF4444]" onClick={removeGuar}><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>
          )}
        </div>
        {data.guarantor && (
          <div className="border border-[#0A192F]/10 rounded-sm p-4 bg-[#F3EFEA]/40 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <HField label="First name" required><Input value={data.guarantor.legal_first_name} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, legal_first_name: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
            <HField label="Last name" required><Input value={data.guarantor.legal_last_name} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, legal_last_name: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
            <HField label="Relationship" required><Input value={data.guarantor.relationship_to_applicant} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, relationship_to_applicant: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
            <HField label="Employer"><Input value={data.guarantor.employer_name || ""} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, employer_name: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
            <HField label="Email" required><Input type="email" value={data.guarantor.email} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, email: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
            <HField label="Phone" required><Input value={data.guarantor.phone} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, phone: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
            <HField label="Monthly gross income ($)" required><Input type="number" value={data.guarantor.monthly_gross_income} onChange={(ev) => setData((d) => ({ ...d, guarantor: { ...d.guarantor, monthly_gross_income: ev.target.value } }))} className="h-11 rounded-sm" /></HField>
          </div>
        )}
      </div>
    </div>
  );
}

function PetVehicleStep({ data, setData }) {
  const addPet = () => setData((d) => ({ ...d, pets: [...d.pets, { species: "", breed: "", name: "", weight_lbs: "", is_service_or_esa: false }] }));
  const addVeh = () => setData((d) => ({ ...d, vehicles: [...d.vehicles, { make: "", model: "", year: "", color: "", license_plate: "", state: "CA" }] }));
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-2xl text-[#0A192F]">Pets & vehicles</h2>
      <div className="bg-[#F3EFEA] p-4 rounded-sm text-xs text-[#4A5568] flex gap-2">
        <AlertCircle className="h-4 w-4 text-[#0A192F] shrink-0 mt-0.5" />
        <span>Service animals and Emotional Support Animals are not "pets" under FEHA and the Fair Housing Act. They cannot be denied or charged pet rent / pet deposit.</span>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Pets / animals</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addPet} data-testid="add-pet"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
        </div>
        {data.pets.map((p, i) => (
          <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 mb-3 bg-[#F3EFEA]/40 grid grid-cols-1 sm:grid-cols-5 gap-4 relative">
            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-[#EF4444]" onClick={() => setData((d) => ({ ...d, pets: d.pets.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
            <HField label="Species"><Input value={p.species} onChange={(ev) => setData((d) => ({ ...d, pets: d.pets.map((x, j) => j === i ? { ...x, species: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Breed"><Input value={p.breed || ""} onChange={(ev) => setData((d) => ({ ...d, pets: d.pets.map((x, j) => j === i ? { ...x, breed: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Name"><Input value={p.name || ""} onChange={(ev) => setData((d) => ({ ...d, pets: d.pets.map((x, j) => j === i ? { ...x, name: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Weight (lbs)"><Input type="number" value={p.weight_lbs} onChange={(ev) => setData((d) => ({ ...d, pets: d.pets.map((x, j) => j === i ? { ...x, weight_lbs: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <div className="flex items-center gap-2 self-end pb-2">
              <Checkbox id={`esa-${i}`} checked={p.is_service_or_esa} onCheckedChange={(v) => setData((d) => ({ ...d, pets: d.pets.map((x, j) => j === i ? { ...x, is_service_or_esa: !!v } : x) }))} />
              <Label htmlFor={`esa-${i}`} className="text-xs">Service / ESA</Label>
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Vehicles</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addVeh}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
        </div>
        {data.vehicles.map((v, i) => (
          <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 mb-3 bg-[#F3EFEA]/40 grid grid-cols-1 sm:grid-cols-6 gap-4 relative">
            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-[#EF4444]" onClick={() => setData((d) => ({ ...d, vehicles: d.vehicles.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
            <HField label="Year"><Input type="number" value={v.year} onChange={(ev) => setData((d) => ({ ...d, vehicles: d.vehicles.map((x, j) => j === i ? { ...x, year: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Make"><Input value={v.make} onChange={(ev) => setData((d) => ({ ...d, vehicles: d.vehicles.map((x, j) => j === i ? { ...x, make: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Model"><Input value={v.model} onChange={(ev) => setData((d) => ({ ...d, vehicles: d.vehicles.map((x, j) => j === i ? { ...x, model: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Color"><Input value={v.color || ""} onChange={(ev) => setData((d) => ({ ...d, vehicles: d.vehicles.map((x, j) => j === i ? { ...x, color: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Plate"><Input value={v.license_plate || ""} onChange={(ev) => setData((d) => ({ ...d, vehicles: d.vehicles.map((x, j) => j === i ? { ...x, license_plate: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="State"><Input value={v.state || ""} onChange={(ev) => setData((d) => ({ ...d, vehicles: d.vehicles.map((x, j) => j === i ? { ...x, state: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
          </div>
        ))}
      </div>
    </div>
  );
}

function RefHistoryStep({ data, update, setData }) {
  const addRef = () => setData((d) => ({ ...d, references: [...d.references, { name: "", relationship: "", phone: "", email: "" }] }));
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-2xl text-[#0A192F]">References & disclosures</h2>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#0A192F]">Personal references</h3>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addRef}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
        </div>
        {data.references.map((r, i) => (
          <div key={i} className="border border-[#0A192F]/10 rounded-sm p-4 mb-3 bg-[#F3EFEA]/40 grid grid-cols-1 sm:grid-cols-4 gap-4 relative">
            {i > 0 && <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-[#EF4444]" onClick={() => setData((d) => ({ ...d, references: d.references.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>}
            <HField label="Name" required><Input value={r.name} onChange={(ev) => setData((d) => ({ ...d, references: d.references.map((x, j) => j === i ? { ...x, name: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Relationship" required><Input value={r.relationship} onChange={(ev) => setData((d) => ({ ...d, references: d.references.map((x, j) => j === i ? { ...x, relationship: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Phone" required><Input value={r.phone} onChange={(ev) => setData((d) => ({ ...d, references: d.references.map((x, j) => j === i ? { ...x, phone: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
            <HField label="Email"><Input type="email" value={r.email || ""} onChange={(ev) => setData((d) => ({ ...d, references: d.references.map((x, j) => j === i ? { ...x, email: ev.target.value } : x) }))} className="h-11 rounded-sm" /></HField>
          </div>
        ))}
      </div>
      <div>
        <h3 className="font-serif text-lg text-[#0A192F] mb-2">Emergency contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <HField label="Name" required><Input value={data.emergency_contact.name} onChange={(ev) => update("emergency_contact.name", ev.target.value)} className="h-11 rounded-sm" /></HField>
          <HField label="Relationship" required><Input value={data.emergency_contact.relationship} onChange={(ev) => update("emergency_contact.relationship", ev.target.value)} className="h-11 rounded-sm" /></HField>
          <HField label="Phone" required><Input value={data.emergency_contact.phone} onChange={(ev) => update("emergency_contact.phone", ev.target.value)} className="h-11 rounded-sm" /></HField>
          <HField label="Email"><Input type="email" value={data.emergency_contact.email || ""} onChange={(ev) => update("emergency_contact.email", ev.target.value)} className="h-11 rounded-sm" /></HField>
        </div>
      </div>
      <div>
        <h3 className="font-serif text-lg text-[#0A192F] mb-2">Self-disclosures</h3>
        <div className="bg-[#F3EFEA] p-4 rounded-sm text-xs text-[#4A5568] mb-3 flex gap-2">
          <AlertCircle className="h-4 w-4 text-[#0A192F] shrink-0 mt-0.5" />
          <span>AB 1418 — We do <strong>not</strong> ask about arrests that did not lead to conviction. A conviction disclosure does not result in automatic denial; we conduct an individual assessment.</span>
        </div>
        <YN label="Have you ever been evicted?" v={data.history.has_been_evicted} onChange={(b) => update("history.has_been_evicted", b)} testid="hist-evict">
          {data.history.has_been_evicted && <Textarea placeholder="Please explain (date, reason, outcome)" value={data.history.eviction_explanation} onChange={(ev) => update("history.eviction_explanation", ev.target.value)} rows={2} className="rounded-sm mt-2" />}
        </YN>
        <YN label="Have you filed for bankruptcy?" v={data.history.has_filed_bankruptcy} onChange={(b) => update("history.has_filed_bankruptcy", b)} testid="hist-bk">
          {data.history.has_filed_bankruptcy && <Textarea placeholder="Please explain (chapter, year, discharged?)" value={data.history.bankruptcy_explanation} onChange={(ev) => update("history.bankruptcy_explanation", ev.target.value)} rows={2} className="rounded-sm mt-2" />}
        </YN>
        <YN label="Do you have any criminal convictions in the last 7 years (excluding sealed/expunged)?" v={data.history.has_conviction} onChange={(b) => update("history.has_conviction", b)} testid="hist-conv">
          {data.history.has_conviction && <Textarea placeholder="You may explain mitigating circumstances. This will be reviewed under AB 1418's individual-assessment standard." value={data.history.conviction_explanation} onChange={(ev) => update("history.conviction_explanation", ev.target.value)} rows={3} className="rounded-sm mt-2" />}
        </YN>
        <YN label="Are you a smoker?" v={data.history.is_smoker} onChange={(b) => update("history.is_smoker", b)} />
        <YN label="Do you receive a housing voucher / Section 8 / subsidy?" v={data.history.receives_housing_subsidy} onChange={(b) => update("history.receives_housing_subsidy", b)} />
      </div>
      <HField label="Desired move-in date"><Input type="date" value={data.desired_move_in} onChange={(ev) => update("desired_move_in", ev.target.value)} className="h-11 rounded-sm max-w-xs" /></HField>
      <HField label="Notes to landlord"><Textarea rows={3} value={data.notes_to_landlord} onChange={(ev) => update("notes_to_landlord", ev.target.value)} className="rounded-sm" /></HField>
    </div>
  );
}

function YN({ label, v, onChange, testid, children }) {
  return (
    <div className="py-3 border-b border-[#0A192F]/10 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-[#0A192F] flex-1">{label}</div>
        <div className="flex gap-2 shrink-0">
          <Button type="button" size="sm" variant={v === true ? "default" : "outline"} onClick={() => onChange(true)} className={`rounded-sm h-8 ${v === true ? "bg-[#0A192F] hover:bg-[#0A192F]/90" : ""}`} data-testid={testid && `${testid}-yes`}>Yes</Button>
          <Button type="button" size="sm" variant={v === false ? "default" : "outline"} onClick={() => onChange(false)} className={`rounded-sm h-8 ${v === false ? "bg-[#0A192F] hover:bg-[#0A192F]/90" : ""}`} data-testid={testid && `${testid}-no`}>No</Button>
        </div>
      </div>
      {children}
    </div>
  );
}

function SignStep({ meta, data, accepted, setAccepted, signedName, setSignedName, esign, setEsign, bg, setBg, credit, setCredit }) {
  const required = meta.disclosures.filter((d) => d.required);
  const optional = meta.disclosures.filter((d) => !d.required);
  const fee = meta.screening.screening_fee_amount;
  const cap = meta.screening.screening_fee_cap_at_send;
  return (
    <div className="space-y-6">
      <h2 className="font-serif text-2xl text-[#0A192F]">Required Disclosures &amp; Signature</h2>
      <p className="text-sm text-[#4A5568]">Please read each disclosure and check the box to acknowledge. All disclosures marked required must be acknowledged before you can submit.</p>

      {fee != null && (
        <div className="bg-[#0A192F] text-white p-5 rounded-sm">
          <div className="font-overline text-white/60 mb-1">Screening fee</div>
          <div className="font-serif text-2xl">${Number(fee).toFixed(2)}</div>
          <p className="text-xs text-white/70 mt-2">Within California's CPI-indexed cap of ${Number(cap || 62.02).toFixed(2)} (Civil Code §1950.6). You are entitled to an itemized receipt and a copy of any consumer report obtained.</p>
        </div>
      )}

      <div className="space-y-3">
        {[...required, ...optional].map((d) => (
          <label key={d.key} className="flex gap-3 items-start p-4 border border-[#0A192F]/10 rounded-sm cursor-pointer hover:bg-[#F3EFEA]/40" data-testid={`disclosure-${d.key}`}>
            <Checkbox checked={!!accepted[d.key]} onCheckedChange={(v) => setAccepted((a) => ({ ...a, [d.key]: !!v }))} className="mt-0.5" />
            <div>
              <div className="font-serif text-base text-[#0A192F]">{d.title}{d.required && <span className="text-[#EF4444] ml-1">*</span>}</div>
              <div className="text-xs text-[#4A5568] leading-relaxed mt-1">{d.text}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="border-t border-[#0A192F]/10 pt-6 space-y-4">
        <h3 className="font-serif text-lg text-[#0A192F]">Authorizations</h3>
        <label className="flex gap-3 items-start cursor-pointer" data-testid="auth-credit">
          <Checkbox checked={credit} onCheckedChange={(v) => setCredit(!!v)} className="mt-0.5" />
          <div className="text-sm text-[#0A192F]">I authorize NTN to obtain a <strong>credit report</strong> for the purpose of evaluating this rental application. (FCRA / ICRAA)</div>
        </label>
        <label className="flex gap-3 items-start cursor-pointer" data-testid="auth-bg">
          <Checkbox checked={bg} onCheckedChange={(v) => setBg(!!v)} className="mt-0.5" />
          <div className="text-sm text-[#0A192F]">I authorize NTN to conduct a <strong>background check</strong>, including criminal history (subject to AB 1418), eviction records, and identity verification.</div>
        </label>
        <label className="flex gap-3 items-start cursor-pointer" data-testid="auth-esign">
          <Checkbox checked={esign} onCheckedChange={(v) => setEsign(!!v)} className="mt-0.5" />
          <div className="text-sm text-[#0A192F]">I consent to <strong>conduct this transaction electronically</strong> and agree my typed name is my legal signature (ESIGN / UETA).</div>
        </label>
      </div>

      <div className="border-t border-[#0A192F]/10 pt-6">
        <h3 className="font-serif text-lg text-[#0A192F] mb-2">Sign</h3>
        <p className="text-xs text-[#4A5568] mb-3">Type your full legal name exactly as on the first page: <strong className="text-[#0A192F]">{data.applicant.legal_first_name} {data.applicant.legal_last_name}</strong></p>
        <Input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Your full legal name" className="h-12 rounded-sm font-serif text-lg" data-testid="signed-name" />
        <p className="text-[10px] text-[#4A5568] font-mono mt-2">When you click submit, your IP address, user-agent, and a UTC timestamp will be permanently attached to this signature.</p>
      </div>
    </div>
  );
}

function DoneScreen({ meta }) {
  return (
    <Shell>
      <div className="max-w-2xl mx-auto bg-white border border-[#0A192F]/10 rounded-sm p-12 text-center" data-testid="apply-done">
        <div className="h-14 w-14 rounded-full bg-[#22C55E]/15 flex items-center justify-center mx-auto mb-6">
          <ShieldCheck className="h-7 w-7 text-[#22C55E]" />
        </div>
        <div className="font-overline text-[#4A5568] mb-3">Application received</div>
        <h1 className="font-serif text-3xl text-[#0A192F] mb-3">Thank you. Your application has been submitted.</h1>
        <p className="text-sm text-[#4A5568] max-w-md mx-auto">
          A signed copy has been emailed to you. {meta.screening.submitted_by_name} will be in touch with next steps.
          You are entitled to a copy of any consumer report obtained from this application.
        </p>
      </div>
    </Shell>
  );
}
